// Per-estimate condition type settings (db/37 TakeoffConditionOverrides): one estimate customises a drawable Item /
// Assembly's canvas + quantity fields without touching the catalog or any other estimate. A stored value wins over the
// catalog; null = inherit; 'none' on a Quantity 2 / 3 calc type = no such quantity in this estimate.
//
// Pure helpers only — the server (sp_GetTakeoffConditionsDrawn, sp_GetEstimateDesignRollup) does the real resolution
// for drawn rows; this merges the same overrides into the TYPE list the canvas pickers and tools read, so the two agree.

import { calcTypeUnit } from "../config/conditionCalcTypes";

export const OVERRIDE_TEXT_FIELDS = ["Quantity1CalcType", "Quantity2CalcType", "Quantity3CalcType", "Color", "Pattern", "Shape"];
export const OVERRIDE_NUM_FIELDS = ["DefaultHeightFt", "DefaultWidthFt", "DefaultThicknessIn", "GridTileWFt", "GridTileHFt", "GridGapIn", "PatternSpacingIn", "RoundToIn", "RoundAreaToSf"];
export const OVERRIDE_FIELDS = [...OVERRIDE_TEXT_FIELDS, ...OVERRIDE_NUM_FIELDS];
const UNIT_FIELDS = ["Quantity2Unit", "Quantity3Unit"];

/** TypeKey of the type an override row belongs to ('item:7' / 'asm:9' — the same key the pickers use). */
export const overrideTypeKey = (row) => (row?.Kind === "item" ? `item:${row.ItemID}` : row?.Kind === "assembly" ? `asm:${row.AssemblyID}` : null);

const isNone = (v) => v === "none";
const nz = (v) => (v === "" || v === undefined ? null : v);

/** Merge the revision's override rows onto the catalog type list. A type with no override is returned untouched. */
export function applyTypeOverrides(types = [], overrides = []) {
  if (!overrides?.length) return types;
  const byKey = new Map(overrides.map(o => [overrideTypeKey(o), o]));
  return types.map(t => {
    const o = byKey.get(t.TypeKey);
    if (!o) return t;
    const out = { ...t };
    const customized = [];
    if (nz(o.NameOverride) !== null) { out._catalogName = t.Name; out.Name = o.NameOverride; } // this estimate's own name for the type
    for (const f of OVERRIDE_FIELDS) {
      const v = nz(o[f]);
      if (v === null) continue;
      customized.push(f);
      out[f] = isNone(v) ? null : v;
    }
    for (const f of UNIT_FIELDS) {
      const v = nz(o[f]);
      if (v === null) continue;
      out[f] = isNone(v) ? null : v;
    }
    out._customized = customized;
    return out;
  });
}

const normNum = (v) => { const n = v === "" || v == null ? null : Number(v); return Number.isFinite(n) ? n : null; };
const normText = (v) => (v === "" || v == null ? null : String(v));

/** A form draft (all strings) from a type's current resolved values. */
export function draftFromType(type = {}) {
  const d = { Name: type.Name ?? "" };
  for (const f of OVERRIDE_TEXT_FIELDS) d[f] = type[f] ?? "";
  for (const f of OVERRIDE_NUM_FIELDS) d[f] = type[f] ?? "";
  return d;
}

/**
 * The payload fields to save so the estimate resolves to `draft`, given the untouched catalog type `raw`. Every
 * override column is present (present-key semantics on the proc): a value that equals the catalog is sent as null,
 * which clears any earlier override; a Quantity 2 / 3 the catalog has but the draft blanks is 'none'.
 */
export function buildOverridePatch(raw = {}, draft = {}) {
  const patch = {};
  // The estimate's own name for the type: a blank or catalog-equal name inherits (null clears).
  const nm = normText(draft.Name)?.trim() || null;
  patch.NameOverride = nm === null || nm === normText(raw.Name) ? null : nm;
  for (const f of OVERRIDE_NUM_FIELDS) {
    const d = normNum(draft[f]), r = normNum(raw[f]);
    patch[f] = d === r ? null : d;   // a blank number means "use the catalog" (null = inherit), so it is never an override
  }
  for (const f of ["Color", "Pattern", "Shape", "Quantity1CalcType"]) {
    const d = normText(draft[f]), r = normText(raw[f]);
    patch[f] = d === null || d === r ? null : d;
  }
  for (const n of [2, 3]) {
    const f = `Quantity${n}CalcType`, d = normText(draft[f]), r = normText(raw[f]);
    patch[f] = d === r ? null : d === null ? "none" : d;
    const style = raw.Style;
    patch[`Quantity${n}Unit`] = patch[f] === null ? null : patch[f] === "none" ? "none" : (calcTypeUnit(style, patch[f]) || null);
  }
  return patch;
}

export const patchIsEmpty = (patch) => Object.values(patch).every(v => v === null);
export const patchesEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** Which fields of a resolved type differ from the catalog — the "customised" list the window shows. */
export const customizedFields = (raw, resolved) => (resolved?._customized || []).filter(f => normText(raw?.[f]) !== normText(resolved?.[f]) || normNum(raw?.[f]) !== normNum(resolved?.[f]));
